import React, { useState, useEffect } from 'react'
import { Input } from 'react-daisyui'
import IconColorPicker from './IconColorPicker'
import iconPackRegistry from './iconPacks/registry'
import { IconDefinition } from './iconPacks/types'

const LogoOptions = ({ logo, onChange }) => {
  const [search, setSearch] = useState('') // Search term for icons
  const [selectedPack, setSelectedPack] = useState(
    logo?.iconPack || 'font-awesome-solid'
  )
  // Get all icons from all packs
  const allIcons = Object.values(iconPackRegistry).flatMap((pack) => pack.icons)
  // Find the initial selected icon from all icons
  const initialSelectedIcon = logo?.icon
    ? allIcons.find(
        (i) =>
          i.name === logo.icon &&
          i.pack === (logo.iconPack || 'font-awesome-solid')
      ) || null
    : null
  const [selectedIcon, setSelectedIcon] = useState<IconDefinition | null>(
    initialSelectedIcon
  )

  const handleIconSelection = (icon: IconDefinition) => {
    setSelectedIcon(icon)
    setSelectedPack(icon.pack)
    onChange({
      icon: icon.name,
      iconPack: icon.pack,
    })
  }

  // Update selected icon when logo changes
  useEffect(() => {
    if (logo?.icon) {
      const icon = allIcons.find(
        (i) =>
          i.name === logo.icon &&
          i.pack === (logo.iconPack || 'font-awesome-solid')
      )
      if (icon) {
        setSelectedIcon(icon)
        setSelectedPack(icon.pack)
      }
    }
  }, [logo?.icon, logo?.iconPack])

  return (
    <div className="space-y-4 p-3 md:p-4 bg-base-100 rounded-lg shadow-lg">
      {/* Icon Pack Selector */}
      <div>
        <label className="label text-sm md:text-base">Icon Pack</label>
        <select
          className="select select-bordered w-full"
          value={selectedPack}
          onChange={(e) => setSelectedPack(e.target.value)}
        >
          {Object.entries(iconPackRegistry).map(([key, pack]) => (
            <option key={key} value={key}>
              {pack.name}
            </option>
          ))}
        </select>
      </div>

      {/* Icon Picker */}
      <div>
        <label className="label text-sm md:text-base">Icon Picker</label>
        <Input
          type="text"
          placeholder="Search icons..."
          className="w-full input input-bordered text-sm md:text-base"
          value={search}
          onChange={(e) => setSearch(e.target.value.toLowerCase())}
        />
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-4 max-h-48 md:max-h-64 overflow-y-scroll border p-2 rounded-lg">
          {allIcons
            .filter(
              (icon) =>
                icon.pack === selectedPack &&
                (search
                  ? (icon.displayName || icon.name)
                      .toLowerCase()
                      .includes(search)
                  : true)
            )
            .map((icon) => (
              <div
                key={`${icon.pack}-${icon.name}`}
                className={`p-2 border rounded-md flex justify-center items-center cursor-pointer ${
                  selectedIcon?.name === icon.name &&
                  selectedIcon?.pack === icon.pack
                    ? 'bg-primary text-white'
                    : ''
                }`}
                onClick={() => handleIconSelection(icon)}
              >
                {iconPackRegistry[icon.pack].getIconComponent(icon, 24)}
              </div>
            ))}
          {allIcons.filter(
            (icon) =>
              icon.pack === selectedPack &&
              (search
                ? (icon.displayName || icon.name).toLowerCase().includes(search)
                : true)
          ).length === 0 && (
            <div className="col-span-full text-center py-8 text-base-content/60">
              No icons found
            </div>
          )}
        </div>
      </div>

      {/* Background Color */}
      <div className="space-y-2">
        <label className="label text-sm md:text-base">Background Color</label>
        <IconColorPicker
          label="Background Color"
          defaultValue={logo?.backgroundColor || '#ffffff'}
          onChange={(value) => onChange({ backgroundColor: value.iconColor })}
        />
      </div>

      {/* Icon Color */}
      <div className="space-y-2">
        <label className="label text-sm md:text-base">Icon Color</label>
        <IconColorPicker
          label="Icon Color"
          defaultValue={logo?.iconColor || '#000000'}
          onChange={(value) => onChange({ iconColor: value.iconColor })}
        />
      </div>

      {/* Exact Icon Size */}
      <div className="space-y-2">
        <label className="label text-sm md:text-base">Icon Size</label>
        <div className="flex items-center gap-2">
          <Input
            type="range"
            className="range range-primary flex-1"
            value={logo?.exactIconSize || 64}
            min="16"
            max="256"
            step="1"
            onChange={(e) =>
              onChange({ exactIconSize: parseInt(e.target.value, 10) })
            }
          />
          <div className="flex items-center gap-1">
            <Input
              type="number"
              className="input input-bordered input-sm w-16 h-8 text-sm"
              value={logo?.exactIconSize || 64}
              min="16"
              max="256"
              onChange={(e) =>
                onChange({ exactIconSize: parseInt(e.target.value, 10) })
              }
            />
            <span className="text-xs whitespace-nowrap">px</span>
          </div>
        </div>
      </div>

      {/* Border Width */}
      <div className="space-y-2">
        <label className="label text-sm md:text-base">Image Border</label>
        <div className="flex items-center gap-2">
          <Input
            type="range"
            min="0"
            max="20"
            value={logo?.borderWidth || 0}
            className="range range-primary flex-1"
            onChange={(e) =>
              onChange({ borderWidth: parseInt(e.target.value, 10) })
            }
          />
          <div className="flex items-center gap-1">
            <Input
              type="number"
              className="input input-bordered input-sm w-16 h-8 text-sm"
              value={logo?.borderWidth || 0}
              min="0"
              max="20"
              onChange={(e) =>
                onChange({ borderWidth: parseInt(e.target.value, 10) })
              }
            />
            <span className="text-xs whitespace-nowrap">px</span>
          </div>
        </div>
      </div>

      {/* Border Radius */}
      <div className="space-y-2">
        <label className="label text-sm md:text-base">Icon Border Radius</label>
        <div className="flex items-center gap-2">
          <Input
            type="range"
            className="range range-primary flex-1"
            value={logo?.borderRadius || 8}
            min="0"
            max="64"
            step="1"
            onChange={(e) =>
              onChange({ borderRadius: parseInt(e.target.value, 10) })
            }
          />
          <div className="flex items-center gap-1">
            <Input
              type="number"
              className="input input-bordered input-sm w-16 h-8 text-sm"
              value={logo?.borderRadius || 8}
              min="0"
              max="64"
              onChange={(e) =>
                onChange({ borderRadius: parseInt(e.target.value, 10) })
              }
            />
            <span className="text-xs whitespace-nowrap">px</span>
          </div>
        </div>
      </div>

      {/* Rotate */}
      <div className="space-y-2">
        <label className="label text-sm md:text-base">Icon Rotation</label>
        <div className="flex items-center gap-2">
          <Input
            type="range"
            min="0"
            max="360"
            value={logo?.rotate || 0}
            className="range range-primary flex-1"
            onChange={(e) => onChange({ rotate: parseInt(e.target.value, 10) })}
          />
          <div className="flex items-center gap-1">
            <Input
              type="number"
              className="input input-bordered input-sm w-16 h-8 text-sm"
              value={logo?.rotate || 0}
              min="0"
              max="360"
              onChange={(e) =>
                onChange({ rotate: parseInt(e.target.value, 10) })
              }
            />
            <span className="text-xs whitespace-nowrap">°</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LogoOptions
